"use client";

import React from "react";
import { useNavigate } from "react-router-dom";

import {
  Heading,
  Flex,
  Link,
  Collection,
  Card,
  Badge,
  Grid,
} from "@aws-amplify/ui-react";
import { Helmet } from "react-helmet";

import { generateClient } from "aws-amplify/api";
import { Schema } from "../../../amplify/data/resource";
import { hasAccess } from "../../acl/groups";
import RoutineCreateForm from "../../ui-components/RoutineCreateForm";
import { DialogTitle, Dialog, DialogContent } from "@mui/material";
import { I18n } from "aws-amplify/utils";
import { FaArrowLeft, FaPlus } from "react-icons/fa6";
import LoadingDialog from "../../components/LoadingDialog";
import PaperComponent from "../../components/PaperComponent";

const client = generateClient<Schema>();

type DanceStyle = Schema["DanceStyle"]["type"];
type Routine = Schema["Routine"]["type"];

interface RoutinesProps {
  groups: string[];
}

const Routines: React.FC<RoutinesProps> = ({ groups }) => {
  const navigate = useNavigate();

  const [danceStyles, setDanceStyles] = React.useState<Map<string, DanceStyle>>(
    new Map()
  );
  const [routineRecords, setRoutineRecords] = React.useState<Routine[]>([]);

  const [open, setOpen] = React.useState(false);

  const [loadingMap, setLoadingMap] = React.useState<{
    [key: string]: boolean;
  }>({});

  const markLoading = (key: string, value: boolean) => {
    setLoadingMap((prev) => ({ ...prev, [key]: value }));
  };

  const onCloseDialog = () => {
    setOpen(false);
  };

  React.useEffect(() => {
    markLoading("danceStyles", true);
    const danceStylesSub = client.models.DanceStyle.observeQuery().subscribe({
      next: ({ items }) => {
        const danceStylesMap = new Map<string, DanceStyle>();
        items.forEach((danceStyle) => {
          danceStylesMap.set(danceStyle.id, danceStyle);
        });
        setDanceStyles(danceStylesMap);
        markLoading("danceStyles", false);
      },
    });
    markLoading("routines", true);
    const routinesSub = client.models.Routine.observeQuery().subscribe({
      next: ({ items }) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        setRoutineRecords([...items]);
        markLoading("routines", false);
      },
    });
    return () => {
      danceStylesSub.unsubscribe();
      routinesSub.unsubscribe();
    };
  }, []);

  return (
    <React.Fragment>
      {Object.values(loadingMap).some((loading) => loading) ? (
        <LoadingDialog loadingMap={loadingMap} />
      ) : (
        <Flex gap="1rem" direction="column">
          <Helmet>
            <title>{I18n.get("Routines")}</title>
          </Helmet>
          <Flex marginTop={"1rem"}>
            <Heading level={1}>{I18n.get("Routines")}</Heading>
          </Flex>
          <Flex>
            <Link onClick={() => navigate(-1)}>
              <FaArrowLeft aria-label={I18n.get("Back")} />
            </Link>
            {hasAccess(groups, "routines", "create") && (
              <Link onClick={() => setOpen(true)}>
                <FaPlus aria-label={I18n.get("Create")} />
              </Link>
            )}
          </Flex>
          <Grid>
            <Collection
              items={routineRecords}
              type="list"
              direction="row"
              wrap="wrap"
              alignContent="flex-start"
              searchPlaceholder={I18n.get("Type to search...")}
              isPaginated
              isSearchable
            >
              {(routine: Routine) => (
                <Card
                  key={routine.id}
                  title={routine.name}
                  variation="outlined"
                  borderRadius="medium"
                  alignSelf="flex-start"
                  width={{ base: "100%", medium: "48.8%", large: "18.9%" }}
                >
                  <Grid marginBottom="1rem">
                    <Heading isTruncated columnStart="1" textAlign="center">
                      <Link href={`/routines/${routine.id}/view`}>
                        {routine.name}
                      </Link>
                    </Heading>
                  </Grid>
                  <Flex
                    direction="column"
                    wrap="wrap"
                    justifyContent="center"
                    marginBottom="1rem"
                  >
                    <Badge
                      backgroundColor={
                        danceStyles.get(routine.danceStyleRoutinesId ?? "")
                          ?.discipline === "Latin"
                          ? "purple.40"
                          : "yellow.40"
                      }
                    >
                      <Link
                        href={`/dance-styles/${routine.danceStyleRoutinesId}/view`}
                        color={"black"}
                      >
                        {
                          danceStyles.get(routine.danceStyleRoutinesId ?? "")
                            ?.name
                        }
                      </Link>
                    </Badge>
                    <Badge
                      backgroundColor={routine.status ? "green.40" : "gray.40"}
                    >
                      {routine.status
                        ? I18n.get("Active")
                        : I18n.get("Disabled")}
                    </Badge>
                  </Flex>
                </Card>
              )}
            </Collection>
          </Grid>
          <Dialog
            open={open}
            onClose={onCloseDialog}
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
          >
            <DialogTitle id="draggable-dialog-title" style={{ cursor: "move" }}>
              {I18n.get("Add Routine")}
            </DialogTitle>
            <DialogContent>
              <RoutineCreateForm
                danceStyles={[...danceStyles.values()]}
                onSuccess={() => setOpen(false)}
              />
            </DialogContent>
          </Dialog>
        </Flex>
      )}
    </React.Fragment>
  );
};

export default Routines;
